import { useCallback, useEffect, useState } from 'react';

import { useFetcher } from '@remix-run/react';
import type { Pagination, PostData } from '@shared/dream-components';

import type { Post } from '~/interfaces/post';

interface Arguments {
  defaultPosts: PostData[];
  pagination: Pagination | null;
  shouldSendPaginationParams?: boolean;
  enabled?: boolean;
  postGroupType: string;
  hasFakeData: boolean;
  tags: string[];
  onPageFetchSuccess?: (newPostIds: string[]) => void;
}

const transformToPostData = (posts: Post[]) =>
  posts.map(
    (post: Post) =>
      ({
        id: post.id,
        web_title: post.web_title,
        web_subtitle: post.web_subtitle,
        comments_state: post.comments_state,
        override_scheduled_at: post.override_scheduled_at,
        audience: post.audience,
        slug: post.slug,
        content_tags: post.content_tags,
        image_url: post.image_url,
        authors: post.authors,
        hide_from_feed: post?.hide_from_feed,
        scheduled_at: post?.scheduled_at,
        is_premium: post?.is_premium,
        estimated_reading_time: post?.estimated_reading_time,
        estimated_reading_time_display: post?.estimated_reading_time_display,
      }) as PostData,
  );

const DEFAULT_PER_PAGE = 5;

const usePaginatedPosts = ({
  defaultPosts = [],
  pagination,
  shouldSendPaginationParams = true,
  enabled = true,
  postGroupType,
  hasFakeData,
  tags,
  onPageFetchSuccess,
}: Arguments) => {
  const fetcher = useFetcher<any>();
  const fetchedData = fetcher?.data;
  const fetchedPagination = fetchedData?.pagination;

  const [isSearching, setIsSearching] = useState(false);
  const [query, setQuery] = useState<string | null>(null);

  const [posts, setPosts] = useState<PostData[]>(defaultPosts);
  const [currentPage, setCurrentPage] = useState((fetchedPagination ? fetchedPagination.page : pagination?.page) || 0);
  const [totalPages, setTotalPages] = useState(
    (fetchedPagination ? fetchedPagination.total_pages : pagination?.total_pages) || 0,
  );
  const [perPage, setPerPage] = useState(fetchedPagination ? fetchedPagination.per_page : pagination?.per_page);

  const fetchPosts = useCallback(
    (searchQuery?: string) => {
      if (!enabled) {
        return;
      }

      const searchParams: any = new URLSearchParams();
      if (searchQuery?.trim()) {
        searchParams.append('q', searchQuery);
      }

      const isPaginationEnabled = shouldSendPaginationParams && currentPage > 0;
      if (isPaginationEnabled) {
        searchParams.append('page', currentPage);
        searchParams.append('per_page', perPage);
      }

      if (tags?.length > 0) {
        searchParams.append('tags', tags.join(','));
      }

      fetcher.load(`/posts?${searchParams.toString()}`);
    },
    [fetcher, currentPage, perPage, shouldSendPaginationParams, enabled, tags],
  );

  const fetchNextPage = useCallback(
    (searchQuery?: string) => {
      if (!enabled) {
        return;
      }

      const searchParams: any = new URLSearchParams();
      if (searchQuery?.trim()) {
        searchParams.append('q', searchQuery);
      }
      searchParams.append('page', currentPage + 1);
      searchParams.append('per_page', typeof perPage === 'number' ? perPage : DEFAULT_PER_PAGE);

      if (tags?.length > 0) {
        searchParams.append('tags', tags.join(','));
      }

      fetcher.load(`/posts?${searchParams.toString()}`);
    },
    [currentPage, fetcher, perPage, enabled, tags],
  );

  const resetSearchState = useCallback(() => {
    setIsSearching(false);
    setCurrentPage(pagination?.page || 0);
    setTotalPages(pagination?.total_pages || 0);
    setPerPage(pagination?.per_page || 0);
    setPosts(defaultPosts);
  }, [pagination, defaultPosts]);

  const handleDebouncedSearchQuery = useCallback(
    (searchQuery: string) => {
      setTimeout(() => {
        setQuery(searchQuery);
      }, 100);
    },
    [setQuery],
  );

  // This is to optimally render the search loading state when a user starts typing to give the impression its searching
  // The debounced state is whats really triggering the query so we dont send too many requests
  const handleNonDebouncedSearchQuery = useCallback(
    (_searchQuery: string) => {
      setIsSearching(true);
      setCurrentPage(0);
      setTotalPages(0);
      setPerPage(0);
      setPosts([]);
    },
    [setIsSearching, setCurrentPage, setTotalPages, setPerPage, setPosts],
  );

  // Reset pagination and posts in state
  // when search query changes
  // and fetch posts
  useEffect(() => {
    // This condition avoids resetting the posts on mount
    // when the data is fake or post group type is any other type
    // but "archive" or "tag_page" or "author_page"
    const isNotTagPage = postGroupType !== 'tag_page';
    const isNotAuthorPage = postGroupType !== 'author_page';
    const isNotArchive = postGroupType !== 'archive';
    const isIncorrectPostGroupType = isNotTagPage && isNotAuthorPage && isNotArchive;

    if (hasFakeData || isIncorrectPostGroupType) {
      return;
    }

    if (typeof query === 'string' && query.trim()) {
      fetchPosts(query);
    }

    if (typeof query === 'string' && !query.trim()) {
      resetSearchState();
    }
  }, [hasFakeData, query]);

  // Update state when data is fetched
  useEffect(() => {
    const fetchedPage = fetchedData?.pagination?.page;

    if (fetchedData && currentPage === 0) {
      setPosts(posts.concat(transformToPostData(fetchedData?.posts)));
      setCurrentPage(currentPage + 1);
      setPerPage(fetchedData?.pagination?.per_page);
      setTotalPages(fetchedData?.pagination?.total_pages);
      setIsSearching(false);
    }

    if (fetchedData && fetchedPage && currentPage && fetchedPage > currentPage) {
      setPosts(posts.concat(transformToPostData(fetchedData?.posts)));
      setCurrentPage(currentPage + 1);
      setPerPage(fetchedData?.pagination?.per_page);
      setTotalPages(fetchedData?.pagination?.total_pages);
      setIsSearching(false);
      onPageFetchSuccess?.((fetchedData?.posts || []).map((post: Post) => post.id));
    }
  }, [fetchedData]);

  return {
    posts,
    showLoadMore: currentPage + 1 <= totalPages,
    isLoading: fetcher?.state !== 'idle',
    postsFetcher: fetcher,
    fetchPosts,
    loadMorePosts: fetchNextPage,
    setCurrentPage,
    setTotalPages,
    setPerPage,
    setPosts,
    isSearching,
    setIsSearching,
    resetSearchState,
    handleDebouncedSearchQuery,
    handleNonDebouncedSearchQuery,
    searchQuery: query,
    setSearchQuery: setQuery,
  };
};

export default usePaginatedPosts;
